import { api3Contracts } from '@api3/dapi-management';
import { Chain } from '@wagmi/chains';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { CaipNetworkId, createAppKit } from '@reown/appkit/react';
import { WagmiProvider } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type ReactNode } from 'react';

if (!process.env.VITE_APP_PROJECT_ID) {
  throw new Error('Missing VITE_APP_PROJECT_ID env variable');
}

const queryClient = new QueryClient();

const localhostChain = {
  nativeCurrency: {
    name: 'Localhost',
    symbol: 'ETH',
    decimals: 18,
  },
  name: 'Localhost',
  network: 'localhost',
  id: 31337,
  rpcUrls: {
    default: {
      http: ['http://127.0.0.1:8545'] as const,
    },
    environment: {
      http: ['http://127.0.0.1:8545'],
    },
    public: {
      http: ['http://127.0.0.1:8545'] as const,
    },
  },
  blockExplorers: {
    default: {
      name: 'Explorer' as const,
      url: 'http://127.0.0.1:8545',
    },
  },
  testnet: true,
};

export const projectId = process.env.VITE_APP_PROJECT_ID;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const arrayFromChains = api3Contracts.viemConfig.chains().map(({ fees, ...rest }) => rest);

// add hardhat to the chains in the development environment
if (process.env.NODE_ENV === 'development') {
  arrayFromChains.push(localhostChain);
}

export const chains: [Chain, ...Chain[]] = [arrayFromChains[0], ...arrayFromChains];

const customRpcUrls: Record<CaipNetworkId, { url: string }[]> = Object.fromEntries(
  api3Contracts.CHAINS.map((chain) => [
    `eip155:${chain.id}`,
    chain.providers
      .map((provider) => provider.rpcUrl)
      .filter((url) => url != null)
      .map((url) => ({ url })),
  ])
);

const metadata = {
  name: 'API3 Market',
  description: 'Oracles that pay you',
  url: 'https://market.api3.org',
  icons: ['https://market.api3.org/favicons/favicon-32.png'],
  verifyUrl: 'https://market.api3.org',
};

const wagmiAdapter = new WagmiAdapter({
  networks: chains,
  projectId,
  customRpcUrls,
  ssr: false,
});

// Wallet IDs can be found here: https://docs.reown.com/cloud/wallets/wallet-list
const WalletIds = {
  METAMASK: 'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96',
  WALLET_CONNECT: 'wallee7c4d26541a7fd84dbdfa9922d3ad21e936e13a7a0e44385d44f006139e44d3btconnect',
  COINBASE: 'fd20dc426fb37566d803205b19bbc1d4096b248ac04548e3cfb6b3a38bd033aa',
  RABBY: '18388be9ac2d02726dbac9777c96efaac06d744b2f6d580fccdd4127a6d01fd1',
};

export const appKit = createAppKit({
  adapters: [wagmiAdapter],
  includeWalletIds: [WalletIds.METAMASK, WalletIds.WALLET_CONNECT, WalletIds.RABBY],
  excludeWalletIds: [WalletIds.COINBASE],
  allWallets: 'ONLY_MOBILE',
  networks: chains,
  customRpcUrls,
  metadata,
  allowUnsupportedChain: true,
  projectId,
  features: {
    email: false,
    socials: false,
    analytics: false,
    pay: false,
    send: false,
    history: false,
    swaps: false,
    onramp: false,
    receive: false,
    emailShowWallets: false,
  },
});

export const AppKitProvider = ({ children }: { children: ReactNode }) => {
  return (
    <WagmiProvider config={wagmiAdapter.wagmiConfig}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </WagmiProvider>
  );
};
